/**
 * VoidLoadModal — "Void load and cancel" (DEC-355 / OPEN-151 PR 2).
 *
 * For a load that should never have existed: a test, a duplicate, the wrong job. POST
 * /v1/jobs/:id/void-capture with { reason (10+), driverDidWork }. The API cancels the job,
 * marks its load voided, stops the driver's clock and tells the driver, and the operator stops
 * seeing the load. Nothing is deleted and the Haul ID is never reused. It refuses an approved
 * ticket; the modal shows the API's own sentence for each refusal verbatim (the same reason
 * StuckJobCancelModal reads the error body itself).
 *
 * Opened from the job detail, the "Load captured — needs completion" stuck row, and the
 * In Review ticket, for the manager tier only (STUCK_CANCEL_ROLES). driverDidWork has neither
 * option pre-selected, as in the stuck cancel.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';

/** The API's minimum (DB CHECK jobs_capture_voided_reason_length_check, migration 0177). */
const REASON_MIN = 10;

const GENERIC_FAILURE = 'Could not void the load. Try again.';

export interface VoidLoadTarget {
  readonly jobId: string;
  readonly haulId: string | null;
}

interface ErrorEnvelope {
  readonly error?: {
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly reason?: unknown }>;
  };
}

interface VoidResponse {
  readonly haulConfirmationEmailsSent?: number;
}

async function readVoidError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const reason = body.error?.fields?.[0]?.reason;
    if (typeof reason === 'string') return reason;
    const message = body.error?.message;
    return typeof message === 'string' ? message : GENERIC_FAILURE;
  } catch {
    return GENERIC_FAILURE;
  }
}

export interface VoidLoadModalProps {
  readonly target: VoidLoadTarget | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called after a successful void so the parent can dismiss itself. */
  readonly onVoided: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

const radioRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  fontSize: '14px',
  color: 'var(--color-text-primary)',
};

export function VoidLoadModal({
  target,
  open,
  onClose,
  onVoided,
}: VoidLoadModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [driverDidWork, setDriverDidWork] = React.useState<boolean | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setDriverDidWork(null);
      setError(null);
    }
  }, [open]);

  const mutation = useMutation<VoidResponse, Error, { jobId: string; payload: string }>({
    mutationFn: async ({ jobId, payload }) => {
      const res = await authedFetch(apiOpts, `/v1/jobs/${jobId}/void-capture`, {
        method: 'POST',
        body: payload,
      });
      if (!res.ok) throw new Error(await readVoidError(res));
      return res.json() as Promise<VoidResponse>;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['inreview'] });
      if ((data.haulConfirmationEmailsSent ?? 0) > 0) {
        toast.success(
          'Load voided and job cancelled. The Haul Confirmation email already sent cannot be recalled.',
        );
      } else {
        toast.success('Load voided and job cancelled');
      }
      onVoided();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const reasonValid = reason.trim().length >= REASON_MIN;
  const canSubmit = reasonValid && driverDidWork !== null && !mutation.isPending;

  const handleConfirm = React.useCallback((): void => {
    if (!reasonValid || driverDidWork === null || target === null) return;
    setError(null);
    mutation.mutate({
      jobId: target.jobId,
      payload: JSON.stringify({ reason: reason.trim(), driverDidWork }),
    });
  }, [reasonValid, driverDidWork, target, reason, mutation]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{`Void load and cancel — ${target?.haulId ?? 'job'}`}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
            Use this only for a load that should never have existed: a test, a duplicate, or the
            wrong job. The job is cancelled, its load is never billed, and the driver is notified.
          </p>
          <p
            role="alert"
            data-testid="void-load-warning"
            style={{
              fontSize: '14px',
              margin: 0,
              padding: '8px 10px',
              borderRadius: '4px',
              border: '1px solid var(--color-danger)',
              color: 'var(--color-text-primary)',
            }}
          >
            The operator will no longer see this load. This can&apos;t be undone.
          </p>

          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={labelStyle}>Did the driver do any work on this job?</legend>
            <div style={radioRowStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="radio"
                  name="void-driver-did-work"
                  checked={driverDidWork === true}
                  onChange={(): void => setDriverDidWork(true)}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="radio"
                  name="void-driver-did-work"
                  checked={driverDidWork === false}
                  onChange={(): void => setDriverDidWork(false)}
                />
                No
              </label>
            </div>
          </fieldset>

          <label htmlFor="void-load-reason" style={labelStyle}>
            Reason
          </label>
          <textarea
            id="void-load-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder={`Why should this load not exist? (min ${String(REASON_MIN)} characters)`}
            style={textareaStyle}
            aria-label="Void reason"
          />
          {reason.trim().length > 0 && !reasonValid && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Reason must be at least {REASON_MIN} characters.
            </span>
          )}
        </div>

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep load
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            style={{
              backgroundColor: 'var(--color-danger)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-danger)',
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {mutation.isPending ? 'Voiding…' : 'Void load and cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
